import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/LegalAdvisors.css';

const disclaimerText = `As per Bar Council of India rules, advocates are not permitted to advertise. Any advocate information displayed on ULJAAPP is shared solely for user awareness, and does not constitute advertisement or solicitation. Legal advisors listed here have registered voluntarily, and ULJAAPP does not promote or endorse any individual advocate.`;
// const disclaimerText = `The Bar Council of India prohibits advocates from soliciting clients or advertising their services in any form. By accessing this platform, G-LawHub, you acknowledge and confirm that you are seeking information about registered legal advisors on your own accord and that there has been no solicitation, advertisement, or inducement by G-LawHub or any of its members.

// All content, profiles, or listings of legal advisors on this platform are provided for informational purposes only, based on details voluntarily submitted by the legal professionals themselves. Nothing on this platform shall be construed as legal advice or as a means of advertisement or promotion of any legal practitioner.

// G-LawHub does not guarantee or endorse the accuracy or completeness of any legal advisor’s profile and shall not be held responsible for any consequences arising from actions taken based on the information available herein.

// The content, layout, and design of this platform are the intellectual property of G-LawHub..`;
const LegalAdvisors = () => {
  const [legalAdvisors, setLegalAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    // Check if disclaimer was accepted in this session
    return sessionStorage.getItem('legalAdvisorsDisclaimerAccepted') === 'true';
  });
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if this is a new login session by comparing stored token timestamp
    const currentToken = localStorage.getItem('token');
    const lastDisclaimerToken = sessionStorage.getItem('disclaimerTokenSession');
    
    // If token changed or is new, reset disclaimer acceptance
    if (currentToken && currentToken !== lastDisclaimerToken) {
      sessionStorage.removeItem('legalAdvisorsDisclaimerAccepted');
      sessionStorage.setItem('disclaimerTokenSession', currentToken);
      setDisclaimerAccepted(false);
    }
  }, []);

  // Add useEffect to fetch current user data
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setUserLoading(false);
        return;
      }

      try {
        const response = await axios.get('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setCurrentUser(null);
      } finally {
        setUserLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const fetchLegalAdvisors = async () => {
      try {
        const response = await fetch('/api/auth/legal-advisors-from-users');
        if (!response.ok) throw new Error('Failed to fetch legal advisors');
        const data = await response.json();
        setLegalAdvisors(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchLegalAdvisors();
  }, []);

  const handleBecomeAdvisor = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    // Since we already have currentUser, we can check directly
    if (currentUser && (currentUser.role === 'LegalAdvisor' || currentUser.enrollmentNumber)) {
      setShowPopup(true);
    } else {
      navigate('/become-legal-advisor');
    }
  };

  // Check if current user is already a legal advisor
  const isCurrentUserLegalAdvisor = currentUser && 
    (currentUser.role === 'LegalAdvisor' || currentUser.enrollmentNumber);

  if (loading || userLoading) return <div>Loading...</div>; 
  if (error) return <div>Error: {error}</div>;

  // Helper to get initials for avatar
  const getInitials = (firstName, lastName) => {
    return (
      (firstName ? firstName[0] : '') +
      (lastName ? lastName[0] : '')
    ).toUpperCase();
  };

  if (!disclaimerAccepted) {
    return (
      <div className="popup-modal">
        <div className="legaladvisors-popup-content">
          <h3 className="disclaimer-title"><span className="notranslate" translate="no" aria-hidden="true">⚖️</span> Disclaimer</h3>
          <p className="disclaimer-text">
            {disclaimerText}
          </p>
          <div className="disclaimer-checkbox-row">
            <input
              type="checkbox"
              id="disclaimer-checkbox"
              className="disclaimer-checkbox"
              checked={disclaimerChecked}
              onChange={e => setDisclaimerChecked(e.target.checked)}
            />
            <label htmlFor="disclaimer-checkbox" className="disclaimer-checkbox-label">
              I accept the above.
            </label>
          </div>
          <button
            className="disclaimer-ok-btn"
            disabled={!disclaimerChecked}
            onClick={() => {
              setDisclaimerAccepted(true);
              // Store acceptance in sessionStorage (resets on browser close/logout)
              sessionStorage.setItem('legalAdvisorsDisclaimerAccepted', 'true');
            }}
          >
            PROCEED TO WEBSITE
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="legalAdvisorsContainer la-page">
      {/* Hero */}
      <section className="la-hero">
        <div className="la-hero-content">
          <h1 className="la-hero-title">
            <span className="la-hero-title-dark">Find Expert</span>
            <span className="la-hero-title-accent">Legal Advisors</span>
          </h1>
          <span className="la-hero-underline" aria-hidden="true"></span>
          <p className="la-hero-sub">
            Connect with verified legal professionals who are here to guide you with trusted legal support.
          </p>
          <div className="la-hero-badges">
            <span className="la-badge">
              <span className="la-badge-icon notranslate" translate="no" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2l7 3v6c0 4.4-3 8.5-7 10-4-1.5-7-5.6-7-10V5l7-3z" fill="#297373"/>
                  <path d="M9.5 12.2l1.8 1.8 3.4-3.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Verified Professionals
            </span>
            <span className="la-badge">
              <span className="la-badge-icon notranslate" translate="no" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="5" y="10" width="14" height="10" rx="2" fill="#297373"/>
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#297373" strokeWidth="1.8" fill="none"/>
                  <circle cx="12" cy="15" r="1.6" fill="#fff"/>
                </svg>
              </span>
              Secure &amp; Confidential
            </span>
            <span className="la-badge">
              <span className="la-badge-icon notranslate" translate="no" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="8" r="3.4" fill="#297373"/>
                  <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" fill="#297373"/>
                </svg>
              </span>
              Expert Legal Guidance
            </span>
          </div>

          {/* Become-a-legal-advisor CTA (only when the user is not already one) */}
          {!isCurrentUserLegalAdvisor && (
            <div className="la-hero-cta">
              <div className="la-hero-cta-text">
                <strong>Join ULJAAPP Global Legal Advisor Network</strong>
                <span>Join our platform and help people find the right legal support.</span>
              </div>
              <button
                type="button"
                className="la-hero-cta-btn"
                onClick={handleBecomeAdvisor}
              >
                Apply Now &rsaquo;
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <div className="la-body">
        <div className="la-section-head">
          <h2 className="la-section-title">Our Legal Advisors</h2>
        </div>

      <div className="advisors-list">
        {legalAdvisors.map((advisor) => (
          <div
            key={advisor._id}
            className="advisor-card enhanced"
            onClick={() => navigate(`/legal-advisors/${advisor._id}`)}
          >
            {/* Desktop: avatar and name side by side */}
            <div className="advisor-avatar-section advisor-avatar-desktop">
              {(advisor.profilePhoto || advisor.avatarUrl) ? (
                <img
                  src={advisor.profilePhoto || advisor.avatarUrl}
                  alt="avatar"
                  className="advisor-avatar-img"
                />
              ) : (
                <div className="advisor-avatar-placeholder notranslate" translate="no">
                  {getInitials(advisor.firstName, advisor.lastName)}
                </div>
              )}
            </div>
            <div className="advisor-details-section">
              {/* Responsive: Show avatar and name in a row on mobile/tablet */}
              <div className="advisor-name-row-mobile">
                <span className="advisor-avatar-section advisor-avatar-mobile">
                  {(advisor.profilePhoto || advisor.avatarUrl) ? (
                    <img
                      src={advisor.profilePhoto || advisor.avatarUrl}
                      alt="avatar"
                      className="advisor-avatar-img"
                    />
                  ) : (
                    <div className="advisor-avatar-placeholder">
                      {getInitials(advisor.firstName, advisor.lastName)}
                    </div>
                  )}
                </span>
                <div className="advisor-name-exp-mobile">
                  <span className="advisor-name notranslate" translate="no">
                    Adv. {advisor.firstName} {advisor.lastName}
                  </span>
                  <span className="advisor-exp">
                    <span role="img" aria-label="experience" className="notranslate" translate="no">⏱️</span>
                    {advisor.experience ? ` ${advisor.experience} year Experience` : ''}
                  </span>
                </div>
              </div>
              <div className="advisor-name-exp">
                <span className="advisor-name notranslate" translate="no">
                  Adv. {advisor.firstName} {advisor.lastName}
                </span>
                <span className="advisor-exp">
                  <span role="img" aria-label="experience" className="notranslate" translate="no">⏱️</span>
                  {advisor.experience ? ` ${advisor.experience} year Experience` : ''}
                </span>
              </div>
              <div className="advisor-location">
                <span className="location-pin-icon notranslate" aria-label="location" translate="no">
                  {/* Red map pin SVG */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="notranslate" translate="no">
                    <circle cx="12" cy="10" r="3" fill="#fff"/>
                    <path d="M12 2C7.03 2 3 6.03 3 11c0 5.25 7.05 10.45 8.13 11.23a1 1 0 0 0 1.13 0C13.95 21.45 21 16.25 21 11c0-4.97-4.03-9-9-9zm0 17.88C9.09 17.09 5 13.92 5 11c0-3.87 3.13-7 7-7s7 3.13 7 7c0 2.92-4.09 6.09-7 8.88z" fill="#e53935"/>
                    <circle cx="12" cy="10" r="3" fill="#e53935"/>
                  </svg>
                </span>
                {advisor.district && advisor.state
                  ? `${advisor.district}, ${advisor.state}`
                  : advisor.location || 'Location not specified'}
              </div>
              <hr className="advisor-divider" />
              <div className="advisor-practice">
                <span className="practice-label">Practice area & skills:</span>{' '}
                {advisor.specialization || advisor.practiceAreas || 'N/A'}
                {advisor.moreSkills && (
                  <>
                    , <span className="advisor-more">More</span>
                  </>
                )}
              </div>
              {/* <div className="advisor-languages">
                <span className="practice-label">Languages Known:</span>{' '}
                {advisor.languagesKnown && advisor.languagesKnown.length > 0
                  ? advisor.languagesKnown.join(', ')
                  : 'N/A'}
              </div>
              <div className="advisor-courts">
                <span className="practice-label">Primary Practice Area(s):</span>{' '}
                {advisor.primaryPracticeAreas && advisor.primaryPracticeAreas.length > 0
                  ? advisor.primaryPracticeAreas.join(', ')
                  : 'N/A'}
              </div> */}
              {/* Show About section if available */}
              {/* {advisor.about && (
                <div className="advisor-about" style={{ marginTop: 6, color: '#1976d2', fontSize: '0.97rem' }}>
                  <span className="practice-label">About:</span> {advisor.about}
                </div>
              )} */}
            </div>
          </div>
        ))}
      </div>
      </div>

      {/* Custom Popup */}
      {showPopup && (
        <div className="popup-modal">
          <div className="popup-content">
            <button className="close-btn notranslate" onClick={() => setShowPopup(false)} translate="no">
              <span className="notranslate" translate="no" aria-hidden="true">&times;</span>
            </button>
            <h3>Already a Legal Advisor</h3>
            <p>You are already registered as a legal advisor!</p>
            <button onClick={() => setShowPopup(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalAdvisors;
